import React, { useState, useRef, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { MapPin, Facebook, Castle, Search, Navigation, X, ZoomIn } from "lucide-react";
import { motion } from "framer-motion";
import PageLayout from "@/components/PageLayout";
import HeroSection from "@/components/HeroSection";
import Section from "@/components/Section";
import SectionHeader from "@/components/SectionHeader";
import { Button } from "@/components/Button";

// ============================================
// IMMAGINI
// ============================================

const HERO_IMAGE = "/images/home-slider-04-castello-torre.jpg";

// ============================================
// GOOGLE MAPS EMBED URL
// ============================================

const GOOGLE_MAPS_EMBED_URL = "https://www.google.com/maps/d/embed?mid=1hD-3uypY8pqwHDjQ22gRDPLc0J4ITQY&ehbc=2E312F";

// ============================================
// DATI CASTELLI - TUTTI I 47 CASTELLI CON IMMAGINI DA GOOGLE MAPS
// ============================================

interface Castello {
  name: string;
  fbLink: string;
  image: string;
  location: string;
  description: string;
}

const tuttiCastelli: Castello[] = [
  {
    name: "Arischia",
    fbLink: "https://www.facebook.com/99castelli/posts/117131221138403",
    image: "/images/castelli/Arischia.webp",
    location: "Arischia, AQ",
    description:
      "Antico borgo fortificato che partecipò alla fondazione dell'Aquila nel 1254. Fu uno dei castelli del quarto di Santa Maria.",
  },
  {
    name: "Assergi",
    fbLink: "https://www.facebook.com/99castelli/posts/119345754250283",
    image: "/images/castelli/Assergi.webp",
    location: "Assergi, AQ",
    description:
      "Città fortificata medievale del XII secolo ai piedi del Gran Sasso. Contribuì alla fondazione dell'Aquila costruendo l'attuale chiesa del Carmine.",
  },
  {
    name: "Barete",
    fbLink:
      "https://www.facebook.com/99castelli/posts/pfbid0ayWZhetYusUiKtRYE6JjnBKHiDbkvUYc7YrfSXZQZ2vit5w2s72PgJQMHNmxKJ38l",
    image: "/images/castelli/Barete.webp",
    location: "Barete, AQ",
    description:
      "Borgo medievale fondatore dell'Aquila nel quarto di San Pietro. Conserva tracce dell'antica struttura fortificata.",
  },
  {
    name: "Barili",
    fbLink: "https://www.facebook.com/99castelli/posts/136107375907454",
    image: "/images/castelli/Barili.webp",
    location: "Barili, AQ",
    description: "Piccolo borgo medievale dell'aquilano con origini legate all'incastellamento del XII secolo.",
  },
  {
    name: "Bariscianello",
    fbLink:
      "https://www.facebook.com/99castelli/posts/pfbid03B4jsUReGwkpZKVjmrL9WoicyyaSKD34Vqmt19xWNy6fbqrj8ZSFrU1n6BCv9pygl",
    image: "/images/castelli/Bariscianello.webp",
    location: "Bariscianello, AQ",
    description: "Piccola frazione con resti di fortificazioni medievali, legata storicamente al vicino Barisciano.",
  },
  {
    name: "Barisciano",
    fbLink: "https://www.facebook.com/99castelli/posts/134673382717520",
    image: "/images/castelli/Barisciano.webp",
    location: "Barisciano, AQ",
    description:
      "Borgo con castello-recinto del XII secolo. Subì l'assedio di Braccio da Montone nel 1424 insieme ad altri castelli della zona.",
  },
  {
    name: "Beffi",
    fbLink: "https://www.facebook.com/99castelli/posts/121221160729409",
    image: "/images/castelli/Beffi.webp",
    location: "Beffi, AQ",
    description:
      "Borgo fortificato del XII secolo con torre pentagonale. Faceva parte della rete difensiva dell'altopiano delle Rocche.",
  },
  {
    name: "Bominaco",
    fbLink: "https://www.facebook.com/99castelli/posts/143951081789750",
    image: "/images/castelli/Bominaco.webp",
    location: "Bominaco, AQ",
    description:
      "Castello-recinto del XII secolo con torre cilindrica, ricostruito nel 1424 dopo la distruzione di Braccio da Montone. Famoso per l'Oratorio di San Pellegrino, la 'Cappella Sistina d'Abruzzo'.",
  },
  {
    name: "Camarda",
    fbLink: "https://www.facebook.com/99castelli/posts/133919086126283",
    image: "/images/castelli/Camarda.webp",
    location: "Camarda, AQ",
    description:
      "Antico castello menzionato già nel IX secolo. Partecipò alla fondazione dell'Aquila nel quarto di Santa Maria.",
  },
  {
    name: "Caporciano",
    fbLink:
      "https://www.facebook.com/99castelli/posts/pfbid02grUsd3917V1ckjN12gYJqfTb2LzPrMu2B8adK52PJ9TSBWVRa9EG6DwnBKD3Wu15l",
    image: "/images/castelli/Caporciano.webp",
    location: "Caporciano, AQ",
    description:
      "Borgo medievale ai margini dell'altopiano di Navelli. Comune di Bominaco con il suo celebre complesso monumentale.",
  },
  {
    name: "Cascina",
    fbLink: "https://www.facebook.com/99castelli/posts/115164268001765",
    image: "/images/castelli/Cascina.webp",
    location: "Cascina, AQ",
    description:
      "Castello fondatore dell'Aquila. Conserva tracce dell'incastellamento medievale del territorio aquilano.",
  },
  {
    name: "Castellacci",
    fbLink:
      "https://www.facebook.com/99castelli/posts/pfbid03B4jsUReGwkpZKVjmrL9WoicyyaSKD34Vqmt19xWNy6fbqrj8ZSFrU1n6BCv9pygl",
    image: "/images/castelli/Castellacci.webp",
    location: "Castellacci, AQ",
    description:
      "Resti di fortificazione medievale nell'entroterra aquilano. Il nome stesso rivela la sua origine castellana.",
  },
  {
    name: "Castello di Ocre",
    fbLink:
      "https://www.facebook.com/99castelli/posts/pfbid02tUzHw9cuJAo2gsuNcoSMhM3Jo3Mg66SQ8Vt5QaGz2LpyeRhXbQ9KkGYHuDBFNRafl",
    image: "/images/castelli/Castello di Ocre.webp",
    location: "Ocre, AQ",
    description:
      "Spettacolare borgo fortificato del XII secolo a 933 m sul Monte Circolo. Conserva 470 m di mura, sette torri e resti della chiesa di San Salvatore con affreschi medievali.",
  },
  {
    name: "Castiglione",
    fbLink: "https://www.facebook.com/99castelli/posts/119889747529217",
    image: "/images/castelli/Castiglione.webp",
    location: "Castiglione, AQ",
    description:
      "Borgo d'altura con origini medievali. Il toponimo indica la presenza di un antico castello o fortilizio.",
  },
  {
    name: "Civitaretenga",
    fbLink:
      "https://www.facebook.com/99castelli/posts/pfbid02E5hTXvnQE1TfvuwVbiZCMJfv9rTp9fERqxDkJ8L8BmgA1rZepv1wUnG7gXLfpYkbl",
    image: "/images/castelli/Civitaretenga.webp",
    location: "Civitaretenga, AQ",
    description:
      "Torre duecentesca quadrangolare di 14 m, unica testimonianza del sistema difensivo medievale. Conserva un antico ghetto ebraico del XII-XV secolo.",
  },
  {
    name: "Collepietro",
    fbLink:
      "https://www.facebook.com/99castelli/posts/pfbid02HtjTNN8dpACb6rg78k4rpsCRBtCA9pYbBMyWphLpYqRd1JC2zjtKLWnUVbRLMKCPl",
    image: "/images/castelli/Collepietro.webp",
    location: "Collepietro, AQ",
    description:
      "Borgo fortificato fondatore dell'Aquila. Nel 1185 era feudo dei Gualtieri signori di Collepietro, parenti dei Conti di Celano.",
  },
  {
    name: "Colli di Barete",
    fbLink:
      "https://www.facebook.com/99castelli/posts/pfbid0XmKCR6wXK6moZDt5WtjkBnZGTKjSPDjjvXox53NJexABwEbUU2WqfmDBMgGq6wPgl",
    image: "/images/castelli/Colli di Barete.webp",
    location: "Colli di Barete, AQ",
    description:
      "Piccolo insediamento medievale nelle vicinanze di Barete, con tracce dell'antico sistema difensivo del territorio.",
  },
  {
    name: "Coppito",
    fbLink: "https://www.facebook.com/99castelli/posts/131889706329221",
    image: "/images/castelli/Coppito.webp",
    location: "Coppito, AQ",
    description:
      "Castello fondatore dell'Aquila nel quarto di San Pietro. Sede della storica Basilica di Collemaggio fondata da Papa Celestino V.",
  },
  {
    name: "Corno",
    fbLink: "https://www.facebook.com/99castelli/posts/126180363566822",
    image: "/images/castelli/Corno.webp",
    location: "Corno, AQ",
    description:
      "Antico castello del contado di Antrodoco che contribuì alla fondazione dell'Aquila con il maggior numero di castelli.",
  },
  {
    name: "Fagnano",
    fbLink: "https://www.facebook.com/99castelli/posts/148602664657925",
    image: "/images/castelli/Fagnano.webp",
    location: "Fagnano Alto, AQ",
    description:
      "Borgo fortificato a forma ellittica in posizione strategica. Il castello conserva mura e torri di difesa del XII secolo.",
  },
  {
    name: "Fontecchio",
    fbLink: "https://www.facebook.com/99castelli/posts/137992292385629",
    image: "/images/castelli/Fontecchio.webp",
    location: "Fontecchio, AQ",
    description:
      "Uno dei Borghi più Belli d'Italia con Torre dell'Orologio del XV secolo. Unico castello a resistere all'assedio di Braccio da Montone nel 1424-25.",
  },
  {
    name: "Forcella",
    fbLink:
      "https://www.facebook.com/99castelli/posts/pfbid031R9EvZfPJscyCvxskyy9x35yHgBJukwbyPR1A4gfaSf7St63NZ2BtsoQrsmFPAVtl",
    image: "/images/castelli/Forcella.webp",
    location: "Forcella, AQ",
    description: "Borgo medievale fondatore dell'Aquila. Conserva testimonianze dell'antico insediamento fortificato.",
  },
  {
    name: "Forfona",
    fbLink:
      "https://www.facebook.com/99castelli/posts/pfbid0UcbarRhDis8ndDACmtxKsdkrEybFuznnxHiwTgmchQ67quMQkSTnzKYcSyUHoEbWl",
    image: "/images/castelli/Forfona.webp",
    location: "Forfona, AQ",
    description:
      "Antica Civita di Bagno, sede dell'importante diocesi di Forcona. Il feudatario Cipriano da Forfona ricostruì il castello di Bominaco nel 1424.",
  },
  {
    name: "Fossa",
    fbLink: "https://www.facebook.com/99castelli/posts/129255283259330",
    image: "/images/castelli/Fossa.webp",
    location: "Fossa, AQ",
    description:
      "Castello-recinto del XII secolo con mastio cilindrico di 17 m. Sorge sull'antica città vestina-romana di Aveia (27 a.C.). Custodisce la chiesa bizantina di Santa Maria ad Cryptas.",
  },
  {
    name: "Goriano Valli",
    fbLink: "https://www.facebook.com/99castelli/posts/121870570664468",
    image: "/images/castelli/Goriano Valli.webp",
    location: "Goriano Valli, AQ",
    description:
      "Antico borgo con torre medievale, fondatore dell'Aquila nel quarto di Santa Giusta. Conserva l'impianto originario del castello.",
  },
  {
    name: "Guasto",
    fbLink: "https://www.facebook.com/99castelli/posts/130868846431307",
    image: "/images/castelli/Guasto.webp",
    location: "Guasto, AQ",
    description: "Piccolo borgo medievale dell'entroterra aquilano con tracce di antiche fortificazioni.",
  },
  {
    name: "Leporanica",
    fbLink: "https://www.facebook.com/99castelli/posts/127781943406664",
    image: "/images/castelli/Leporanica.webp",
    location: "Leporanica, AQ",
    description: "Insediamento medievale nel territorio aquilano. Conserva elementi dell'antico sistema difensivo.",
  },
  {
    name: "Navelli",
    fbLink:
      "https://www.facebook.com/99castelli/posts/pfbid0VoVDeE6DLrDpzzos5LQuwFgfrxVm7WCSjdLLYJesjqnoJnTmLEmS7kr459yM2HWKl",
    image: "/images/castelli/Navelli.webp",
    location: "Navelli, AQ",
    description:
      "Uno dei Borghi più Belli d'Italia, con Palazzo Santucci del XVII secolo su rovine del castello medievale. Capitale dello zafferano DOP e resistette a Braccio da Montone nel 1423.",
  },
  {
    name: "Pescomaggiore",
    fbLink: "https://www.facebook.com/99castelli/posts/116179307900261",
    image: "/images/castelli/Pescomaggiore.webp",
    location: "Pescomaggiore, AQ",
    description:
      "Borgo medievale fondatore dell'Aquila arroccato su uno sperone roccioso. Gravemente danneggiato dal terremoto del 2009.",
  },
  {
    name: "Picenze",
    fbLink:
      "https://www.facebook.com/99castelli/posts/pfbid04gpHNuH8f4FxvJW4vkAAfQUL51ECKXZAi2aGdFdSt24mPt8JMJ4ZGxPfD5XzuSgHl",
    image: "/images/castelli/Picenze.webp",
    location: "Picenze, AQ",
    description: "Antico castello del territorio aquilano con tracce delle fortificazioni medievali.",
  },
  {
    name: "Piscignola",
    fbLink: "https://www.facebook.com/99castelli/posts/118641470987378",
    image: "/images/castelli/Piscignola.webp",
    location: "Piscignola, AQ",
    description:
      "Castello del contado di Antrodoco che partecipò alla fondazione dell'Aquila con uno dei maggiori contributi.",
  },
  {
    name: "Pizzoli",
    fbLink: "https://www.facebook.com/99castelli/posts/139801592204699",
    image: "/images/castelli/Pizzoli.webp",
    location: "Pizzoli, AQ",
    description:
      "Castello Dragonetti-De Torres del XVI secolo con torre pentagonale medievale del XII secolo. Fu castello fondatore dell'Aquila nel quarto di Santa Maria.",
  },
  {
    name: "Poggio Picenze",
    fbLink:
      "https://www.facebook.com/99castelli/posts/pfbid02DzeQLHiAE8tsAxpEnWtbpsXgZ7fnBXGCD3qjveyoWsXSTMdAh21SwMmDHV2dzC6dl",
    image: "/images/castelli/Poggio Picenze.webp",
    location: "Poggio Picenze, AQ",
    description:
      "Borgo medievale con resti del castello. Rientra nell'area di produzione dello zafferano DOP dell'Aquila.",
  },
  {
    name: "Preturo",
    fbLink:
      "https://www.facebook.com/99castelli/posts/pfbid028q6zVgxFjKyRZw5D16eR8xaY5424sVFjhuTDJaePE4WLweXVyLVn79w1jSwHV1Dfl",
    image: "/images/castelli/Preturo.webp",
    location: "Preturo, AQ",
    description:
      "Castello fondatore dell'Aquila nel quarto di San Marciano. Menzionato tra i primi castelli del territorio nel IX secolo.",
  },
  {
    name: "Rascino",
    fbLink: "https://www.facebook.com/99castelli/posts/120546594130199",
    image: "/images/castelli/Rascino.webp",
    location: "Rascino, AQ",
    description: "Borgo medievale che partecipò alla fondazione dell'Aquila. Conserva tracce dell'antico castello.",
  },
  {
    name: "Rocca Santo Stefano",
    fbLink: "https://www.facebook.com/99castelli/posts/146570588194466",
    image: "/images/castelli/Rocca Santo Stefano.webp",
    location: "Rocca Santo Stefano, AQ",
    description:
      "Antico insediamento fortificato con la caratteristica struttura a rocca tipica del territorio abruzzese.",
  },
  {
    name: "Roccapreturo",
    fbLink: "https://www.facebook.com/99castelli/posts/122872800564245",
    image: "/images/castelli/Roccapreturo.webp",
    location: "Roccapreturo, AQ",
    description: "Borgo medievale con rocca in posizione dominante. Faceva parte del sistema difensivo della valle.",
  },
  {
    name: "San Benedetto in Perillis",
    fbLink:
      "https://www.facebook.com/99castelli/posts/pfbid02W46aYZ15LZPBYk2K4i7CnZVpbJAwVefbvexHL8eroTyUnHe7rqnhvtQwfRYs5qr2l",
    image: "/images/castelli/San Benedetto in Perillis.webp",
    location: "San Benedetto in Perillis, AQ",
    description:
      "Monastero benedettino dell'VIII secolo con torre di fortificazione. Importante centro monastico dell'altopiano di Navelli.",
  },
  {
    name: "San Marco",
    fbLink:
      "https://www.facebook.com/99castelli/posts/pfbid0JNUQ54jZ3wow9k9oeDYZrr5KgvAddj8FoyDwQrd66VLCJ6HWthu5gzUUaxeLcyZ5l",
    image: "/images/castelli/San Marco.webp",
    location: "San Marco, AQ",
    description: "Piccolo borgo medievale dell'entroterra aquilano con elementi dell'antico insediamento fortificato.",
  },
  {
    name: "San Pietro della Ienca",
    fbLink:
      "https://www.facebook.com/99castelli/posts/pfbid022AUnUxVF1J94UW2D8Ut9Zz2qhc2YexzUE8ns33Ln5Kwci5NfGmv5FEbj8pgCKoLVl",
    image: "/images/castelli/San Pietro della Ienca.webp",
    location: "San Pietro della Ienca, AQ",
    description:
      "Borgo montano con la chiesetta di San Pietro, amata da Papa Giovanni Paolo II che la visitò più volte durante i suoi soggiorni sul Gran Sasso.",
  },
  {
    name: "San Pio delle Camere",
    fbLink: "https://www.facebook.com/99castelli/posts/133229622861896",
    image: "/images/castelli/San Pio delle Camere.webp",
    location: "San Pio delle Camere, AQ",
    description:
      "Castello-recinto del XII secolo con rara torre a puntone (quadrato + triangolo). Domina la Piana di Navelli dal Monte Gentile. Distrutto da Braccio da Montone nel 1424.",
  },
  {
    name: "San Silvestro",
    fbLink: "https://www.facebook.com/99castelli/posts/118158461035679",
    image: "/images/castelli/San Silvestro.webp",
    location: "San Silvestro, AQ",
    description: "Castello fondatore dell'Aquila nel quarto di Santa Maria. Conserva l'impianto medievale originario.",
  },
  {
    name: "San Vittorino",
    fbLink:
      "https://www.facebook.com/99castelli/posts/pfbid02mWpMptf7Dmk1diwnxPxsuV7yedAjR9dGZuRqr8S2ETQ5yEojQBrhaaRSPLE9NfjZl",
    image: "/images/castelli/San Vittorino.webp",
    location: "San Vittorino, AQ",
    description:
      "Sito di grande importanza storica con catacombe paleocristiane e resti dell'antica Amiternum romana, città natale dello storico Sallustio.",
  },
  {
    name: "Sant'Eusanio",
    fbLink: "https://www.facebook.com/99castelli/posts/124907023694156",
    image: "/images/castelli/SantEusanio.webp",
    location: "Sant'Eusanio Forconese, AQ",
    description:
      "Castello medievale su un colle che domina l'abitato. Menzionato nel X secolo, potenziato da quattro torri di cui rimane il torrione settentrionale.",
  },
  {
    name: "Stiffe",
    fbLink: "https://www.facebook.com/99castelli/posts/142004898651035",
    image: "/images/castelli/Stiffe.webp",
    location: "Stiffe, AQ",
    description:
      "Antico borgo del X secolo con chiesa di Sant'Andrea. Fu uno dei tre castelli a non arrendersi a Braccio da Montone nel 1423-24. Famoso per le sue grotte carsiche.",
  },
  {
    name: "Tione",
    fbLink: "https://www.facebook.com/99castelli/posts/124076753777183",
    image: "/images/castelli/Tione.webp",
    location: "Tione degli Abruzzi, AQ",
    description:
      "Borgo medievale fondatore dell'Aquila nel quarto di Santa Giusta. Conserva tracce dell'antico castello.",
  },
  {
    name: "Torretta di Cese",
    fbLink:
      "https://www.facebook.com/99castelli/posts/pfbid031R9EvZfPJscyCvxskyy9x35yHgBJukwbyPR1A4gfaSf7St63NZ2BtsoQrsmFPAVtl",
    image: "/images/castelli/Torretta di Cese.webp",
    location: "Torretta di Cese, AQ",
    description: "Torre medievale di avvistamento facente parte del sistema difensivo del territorio aquilano.",
  },
];

// Funzione per generare URL Google Maps Directions
const getGoogleMapsDirectionsUrl = (location: string) => {
  const destination = encodeURIComponent(location + ", Italy");
  return `https://www.google.com/maps/dir/?api=1&destination=${destination}`;
};

// ============================================
// COMPONENTE LIGHTBOX
// ============================================

interface LightboxProps {
  image: string;
  title: string;
  onClose: () => void;
}

const Lightbox: React.FC<LightboxProps> = ({ image, title, onClose }) => {
  // Chiudi con ESC
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleEsc);
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", handleEsc);
      document.body.style.overflow = "unset";
    };
  }, [onClose]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-4"
      onClick={onClose}
    >
      {/* Pulsante chiudi */}
      <button
        onClick={onClose}
        className="absolute top-4 right-4 z-10 p-2 bg-white/10 hover:bg-white/20 rounded-full transition-colors"
        aria-label="Chiudi"
      >
        <X size={32} className="text-white" />
      </button>

      {/* Titolo */}
      <div className="absolute top-4 left-4 z-10">
        <h3 className="text-white text-xl font-heading font-bold drop-shadow-lg">{title}</h3>
      </div>

      {/* Immagine */}
      <motion.img
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.3 }}
        src={image}
        alt={title}
        className="max-w-full max-h-[90vh] object-contain rounded-lg shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      />

      {/* Istruzioni */}
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white/60 text-sm">
        Premi ESC o clicca fuori per chiudere
      </div>
    </motion.div>
  );
};

// ============================================
// COMPONENTE CARD CASTELLO
// ============================================

interface CastleCardProps {
  castello: Castello;
  index: number;
  onImageClick: (castello: Castello) => void;
}

const CastleCard: React.FC<CastleCardProps> = ({ castello, index, onImageClick }) => {
  const [imageError, setImageError] = useState(false);

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.4, delay: index * 0.03 }}
      className="group bg-white rounded-xl overflow-hidden shadow-elegant hover:shadow-elegant-lg transition-all duration-300 border border-gray-100"
    >
      {/* Immagine - Cliccabile per aprire lightbox */}
      <div
        className="relative aspect-[4/3] overflow-hidden bg-gradient-to-br from-primary/20 to-primary/5 cursor-pointer"
        onClick={() => !imageError && onImageClick(castello)}
      >
        {!imageError ? (
          <>
            <img
              src={castello.image}
              alt={`Castello di ${castello.name}`}
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
              loading="lazy"
              onError={() => setImageError(true)}
            />
            {/* Icona zoom su hover */}
            <div className="absolute top-3 right-3 p-2 bg-black/40 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300">
              <ZoomIn size={20} className="text-white" />
            </div>
          </>
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center bg-gradient-to-br from-primary/10 to-secondary/10">
            <Castle size={48} className="text-primary/40 mb-2" />
            <span className="text-sm text-text-muted">Immagine non disponibile</span>
          </div>
        )}

        {/* Overlay gradient */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />

        {/* Nome castello su immagine */}
        <div className="absolute bottom-0 left-0 right-0 p-4">
          <h3 className="text-xl font-heading font-bold text-white drop-shadow-lg">{castello.name}</h3>
          <p className="text-white/80 text-sm flex items-center gap-1 mt-1">
            <MapPin size={14} />
            {castello.location}
          </p>
        </div>
      </div>

      {/* Descrizione */}
      <div className="px-4 py-3 border-b border-gray-100">
        <p className="text-text-light text-sm leading-relaxed">{castello.description}</p>
      </div>

      {/* Azioni */}
      <div className="p-4 flex gap-3">
        {/* Pulsante Facebook */}
        <a
          href={castello.fbLink}
          target="_blank"
          rel="noopener noreferrer"
          className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-[#1877F2] hover:bg-[#166FE5] text-white rounded-lg font-medium transition-colors text-sm"
        >
          <Facebook size={18} />
          <span>Vedi su Facebook</span>
        </a>

        {/* Pulsante Google Maps Directions */}
        <a
          href={getGoogleMapsDirectionsUrl(castello.location)}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center justify-center gap-2 px-4 py-2.5 bg-primary hover:bg-primary/90 text-white rounded-lg font-medium transition-colors"
          title="Indicazioni stradali"
        >
          <Navigation size={18} />
        </a>
      </div>
    </motion.div>
  );
};

// ============================================
// COMPONENTE STATISTICHE
// ============================================

const StatsSection: React.FC = () => {
  const stats = [
    { value: "47", label: "Castelli Visitati" },
    { value: "99", label: "Castelli Storici" },
    { value: "3", label: "Anni di Attività" },
    { value: "28", label: "Escursioni Totali" },
  ];

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      {stats.map((stat, index) => (
        <motion.div
          key={stat.label}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: index * 0.1 }}
          className="bg-white rounded-xl p-6 shadow-elegant text-center border border-gray-100"
        >
          <div className="text-4xl font-heading font-bold text-primary mb-2">{stat.value}</div>
          <div className="text-sm text-text-light">{stat.label}</div>
        </motion.div>
      ))}
    </div>
  );
};

// ============================================
// COMPONENTE FILTRO LETTERE
// ============================================

const LetterFilter: React.FC<{
  selectedLetter: string | null;
  onSelectLetter: (letter: string | null) => void;
  availableLetters: string[];
}> = ({ selectedLetter, onSelectLetter, availableLetters }) => {
  const allLetters = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");

  return (
    <div className="flex flex-wrap justify-center gap-1 mb-8">
      <button
        onClick={() => onSelectLetter(null)}
        className={`px-3 py-1.5 rounded-lg font-medium text-sm transition-all ${
          selectedLetter === null ? "bg-primary text-white" : "bg-gray-100 text-text-light hover:bg-gray-200"
        }`}
      >
        Tutti
      </button>
      {allLetters.map((letter) => {
        const isAvailable = availableLetters.includes(letter);
        return (
          <button
            key={letter}
            onClick={() => isAvailable && onSelectLetter(letter)}
            disabled={!isAvailable}
            className={`w-9 h-9 rounded-lg font-medium text-sm transition-all ${
              selectedLetter === letter
                ? "bg-primary text-white"
                : isAvailable
                  ? "bg-gray-100 text-text-light hover:bg-gray-200"
                  : "bg-gray-50 text-gray-300 cursor-not-allowed"
            }`}
          >
            {letter}
          </button>
        );
      })}
    </div>
  );
};

// ============================================
// COMPONENTE PRINCIPALE
// ============================================

const Castelli: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedLetter, setSelectedLetter] = useState<string | null>(null);
  const [lightboxCastello, setLightboxCastello] = useState<Castello | null>(null);
  const mapRef = useRef<HTMLDivElement>(null);
  const cardsRef = useRef<HTMLDivElement>(null);

  // Leggi il parametro di ricerca dall'URL
  useEffect(() => {
    const searchQuery = searchParams.get("search");
    if (searchQuery) {
      setSearchTerm(searchQuery);
      setSelectedLetter(null);
      // Rimuovi il parametro dall'URL dopo averlo letto
      setSearchParams({});
      // Scrolla alla sezione dei castelli dopo un breve delay
      setTimeout(() => {
        cardsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 300);
    }
  }, [searchParams, setSearchParams]);

  // Ottieni lettere disponibili
  const availableLetters = [...new Set(tuttiCastelli.map((c) => c.name[0].toUpperCase()))].sort();

  // Filtra castelli
  const filteredCastelli = tuttiCastelli.filter((c) => {
    const searchLower = searchTerm.toLowerCase();
    const nameLower = c.name.toLowerCase();
    // Cerca in entrambe le direzioni: il termine nel nome O il nome nel termine
    const matchesSearch = nameLower.includes(searchLower) || searchLower.includes(nameLower);
    const matchesLetter = selectedLetter ? c.name[0].toUpperCase() === selectedLetter : true;
    return matchesSearch && matchesLetter;
  });

  return (
    <PageLayout>
      {/* Hero Section */}
      <HeroSection
        backgroundImage={HERO_IMAGE}
        title="I 99 Castelli"
        subtitle="Alla scoperta del patrimonio castellare dell'aquilano, tra storia, leggende e panorami mozzafiato"
        height="medium"
        overlay
        centered
      />

      {/* Intro */}
      <Section background="cream">
        <div className="max-w-4xl mx-auto text-center">
          <SectionHeader
            title="Un Patrimonio Unico"
            subtitle="L'Abruzzo custodisce uno dei patrimoni castellari più ricchi d'Italia"
          />
          <p className="text-text-light font-body leading-relaxed mb-6 text-lg">
            La leggenda narra che la città dell'<strong className="text-primary">Aquila</strong> fu fondata nel XIII
            secolo dall'unione di <strong className="text-primary">99 castelli</strong>, ognuno dei quali portò una
            piazza, una fontana e una chiesa. Ancora oggi, il territorio aquilano è punteggiato da decine di
            fortificazioni medievali, torri di avvistamento e borghi murati.
          </p>
          <p className="text-text-light font-body leading-relaxed">
            Sfoglia le schede dei castelli visitati: puoi vedere il post dedicato su Facebook o ottenere le indicazioni
            stradali per raggiungerli.
          </p>
        </div>
      </Section>

      {/* Statistiche */}
      <Section background="white">
        <div className="max-w-5xl mx-auto">
          <StatsSection />
        </div>
      </Section>

      {/* Cards Castelli */}
      <Section background="gray">
        <div ref={cardsRef} className="max-w-7xl mx-auto">
          <SectionHeader
            title="I Castelli Visitati"
            subtitle="Clicca su un castello per vedere il post Facebook o ottenere le indicazioni stradali"
          />

          {/* Barra di ricerca */}
          <div className="max-w-md mx-auto mb-6">
            <div className="relative">
              <Search size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-text-muted" />
              <input
                type="text"
                placeholder="Cerca un castello..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setSelectedLetter(null);
                }}
                className="w-full pl-12 pr-4 py-3 rounded-full border border-gray-200 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all bg-white"
              />
            </div>
          </div>

          {/* Filtro lettere */}
          <LetterFilter
            selectedLetter={selectedLetter}
            onSelectLetter={(letter) => {
              setSelectedLetter(letter);
              setSearchTerm("");
            }}
            availableLetters={availableLetters}
          />

          {/* Risultati count */}
          <div className="text-center mb-8">
            <span className="text-text-muted text-sm">
              {filteredCastelli.length === tuttiCastelli.length
                ? `Mostrando tutti i ${filteredCastelli.length} castelli`
                : `${filteredCastelli.length} castell${filteredCastelli.length === 1 ? "o" : "i"} trovati`}
            </span>
          </div>

          {/* Griglia Cards */}
          {filteredCastelli.length > 0 ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {filteredCastelli.map((castello, index) => (
                <CastleCard
                  key={castello.name}
                  castello={castello}
                  index={index}
                  onImageClick={(c) => setLightboxCastello(c)}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <Castle size={64} className="mx-auto text-text-muted mb-4" />
              <p className="text-text-light text-lg">Nessun castello trovato</p>
              <button
                onClick={() => {
                  setSearchTerm("");
                  setSelectedLetter(null);
                }}
                className="mt-4 text-primary hover:underline"
              >
                Mostra tutti i castelli
              </button>
            </div>
          )}

          {/* Legenda */}
          <div className="mt-10 flex flex-wrap items-center justify-center gap-6 text-sm text-text-muted">
            <span className="flex items-center gap-2">
              <div className="w-8 h-8 bg-black/40 rounded-lg flex items-center justify-center">
                <ZoomIn size={16} className="text-white" />
              </div>
              Ingrandisci immagine
            </span>
            <span className="flex items-center gap-2">
              <div className="w-8 h-8 bg-[#1877F2] rounded-lg flex items-center justify-center">
                <Facebook size={16} className="text-white" />
              </div>
              Apri post Facebook
            </span>
            <span className="flex items-center gap-2">
              <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
                <Navigation size={16} className="text-white" />
              </div>
              Indicazioni stradali
            </span>
          </div>
        </div>
      </Section>

      {/* Mappa Interattiva */}
      <Section background="white" noPadding>
        <div ref={mapRef} className="w-full">
          {/* Header Mappa */}
          <div className="container-custom py-12">
            <SectionHeader title="Mappa Interattiva" subtitle="Scopri la posizione dei castelli sul territorio" />

            {/* Info mappa */}
            <div className="max-w-3xl mx-auto mb-6">
              <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                <p className="text-text-light text-sm text-center">
                  <strong>Suggerimento:</strong> Clicca sui segnaposti verdi per vedere i dettagli di ogni castello. Usa
                  i controlli della mappa per zoomare e navigare.
                </p>
              </div>
            </div>
          </div>

          {/* Mappa Google Embed - Full Width */}
          <div className="w-full bg-gray-100">
            <iframe
              src={GOOGLE_MAPS_EMBED_URL}
              width="100%"
              height="600"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Mappa interattiva dei 99 Castelli"
              className="w-full"
            />
          </div>

          {/* Note sotto mappa */}
          <div className="container-custom py-6">
            <div className="max-w-3xl mx-auto text-center">
              <p className="text-sm text-text-muted italic">
                Sono ancora pochi, ma ne segnerò altri man mano che li visiteremo insieme!
              </p>
            </div>
          </div>
        </div>
      </Section>

      {/* CTA */}
      <Section background="primary">
        <div className="text-center max-w-3xl mx-auto">
          <SectionHeader
            title="Esplora i Castelli con Noi"
            subtitle="Partecipa alle nostre escursioni guidate e scopri la storia dei castelli abruzzesi"
            light
          />
          <p className="text-white/80 mb-8 text-lg">
            Organizziamo escursioni a piedi e in bicicletta per visitare i castelli del territorio aquilano. Tutte le
            attività sono coperte da assicurazione CSI.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button href="/attivita" variant="secondary" size="lg">
              Vedi le Escursioni
            </Button>
            <Button href="/tesseramento" variant="outlineLight" size="lg">
              Diventa Socio
            </Button>
          </div>
        </div>
      </Section>

      {/* Lightbox */}
      {lightboxCastello && (
        <Lightbox
          image={lightboxCastello.image}
          title={`Castello di ${lightboxCastello.name}`}
          onClose={() => setLightboxCastello(null)}
        />
      )}
    </PageLayout>
  );
};

export default Castelli;
